import React, { useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import styled from 'styled-components';

import { IconButton } from '@material-ui/core';
import {
  AddCircleOutline,
  DragIndicator,
  RemoveCircleOutlineOutlined
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { ConfigHeader, ConfigList, ConfigListItem } from '@sierralabs/forms-ui';
import ConfigCard from '@sierralabs/forms-ui/dist/config/components/ConfigCard';
import { Shape, StateNode, WorkflowInvoke } from '@sierralabs/workflow';

import WorkflowConfigInvokeActionDialog from '../dialogs/WorkflowConfigInvokeActionDialog';
import servicePlugins from '../plugins/services';

const StyledConfigListItem = styled(ConfigListItem)`
  .plugin-display {
    padding-left: 22px;
  }
`;

export interface StateInvokeActionEditorProps {
  targetEntity?: string;
  shape: Shape<StateNode<any, any>>;
  onChange?: (invoke: WorkflowInvoke) => void;
}

export const StateInvokeActionEditor: React.FC<StateInvokeActionEditorProps> =
  ({ targetEntity, shape, onChange }) => {
    const stateNode = shape.data as StateNode;
    stateNode.invoke = stateNode.invoke
      ? Array.isArray(stateNode.invoke)
        ? stateNode.invoke
        : [stateNode.invoke]
      : [];
    const invokeArray = stateNode.invoke as WorkflowInvoke[];

    const [editAction, setEditAction] = useState<WorkflowInvoke | null>();
    return (
      <ConfigCard>
        <ConfigHeader
          end={
            <IconButton size='small' onClick={() => setEditAction(null)}>
              <AddCircleOutline />
            </IconButton>
          }
        >
          Actions
        </ConfigHeader>
        <DragDropContext
          onDragEnd={result => {
            const { source, destination } = result;
            // dropped outside the list
            if (!destination) {
              return;
            }
            const [action] = invokeArray.splice(source.index, 1);
            invokeArray.splice(destination.index, 0, action);
            onChange?.(action);
          }}
        >
          <Droppable droppableId='action'>
            {provided => (
              <ConfigList className='action-list' ref={provided.innerRef}>
                {invokeArray.map((action, index) => {
                  const plugin = servicePlugins.find(
                    plugin => plugin.src === action.src
                  );
                  return (
                    <Draggable
                      key={index}
                      index={index}
                      draggableId={`draggable-${index}`}
                    >
                      {(provided, snapshot) => (
                        <StyledConfigListItem
                          hover
                          className={snapshot.isDragging ? 'dragging' : ''}
                          ref={provided.innerRef}
                          icon={<DragIndicator />}
                          iconProps={provided.dragHandleProps}
                          {...provided.draggableProps}
                          primary={plugin?.name || action.src}
                          secondaryAction={
                            <IconButton
                              size='small'
                              onClick={e => {
                                e.stopPropagation();
                                invokeArray.splice(index, 1);
                                onChange?.(action);
                              }}
                            >
                              <RemoveCircleOutlineOutlined />
                            </IconButton>
                          }
                          onClick={() => setEditAction(action)}
                        >
                          <div className='plugin-display'>
                            {plugin ? (
                              <plugin.display invoke={action} />
                            ) : (
                              <Alert severity='error'>
                                Invoke Service "{action.src}" plugin not found.
                              </Alert>
                            )}
                          </div>
                        </StyledConfigListItem>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </ConfigList>
            )}
          </Droppable>
        </DragDropContext>
        {(editAction || editAction === null) && (
          <WorkflowConfigInvokeActionDialog
            stateNode={stateNode}
            targetEntity={targetEntity}
            invoke={editAction}
            open={Boolean(editAction || editAction === null)}
            onCancel={() => setEditAction(undefined)}
            onChange={action => {
              setEditAction(undefined);
              if (editAction) {
                const index = invokeArray.indexOf(editAction);
                if (index > -1) {
                  invokeArray[index] = action;
                } else {
                  invokeArray.push(action);
                }
              } else {
                invokeArray.push(action);
              }
              onChange?.(action);
              // if (editAction) {
              //   const index = actions.indexOf(editAction);
              //   if (element) {
              //     updateElementAction(index, action);
              //   } else {
              //     updateSectionAction(index, action);
              //   }
              // } else {
              //   if (element) {
              //     addElementAction(action);
              //   } else {
              //     addSectionAction(action);
              //   }
              // }
            }}
          />
        )}
      </ConfigCard>
    );
  };

export default StateInvokeActionEditor;
